import { useState } from "react";
import { useAppStore } from "@/lib/store";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Bot, Lock, Loader2, AlertCircle } from "lucide-react";
import { api } from "../../lib/api/client";

export function TokenGate() {
  const { apiToken, apiUrl, setApiToken, setApiUrl } = useAppStore();
  const [token, setToken] = useState(apiToken || "");
  const [url, setUrl] = useState(apiUrl || "http://localhost:8080");
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!token.trim()) {
      setError("Please enter an API token");
      return;
    }

    const apiUrlToUse = url.trim() || "http://localhost:8080";
    setIsValidating(true);

    try {
      // Temporarily set the token and URL to test the connection
      const testToken = token.trim();
      const originalToken = useAppStore.getState().apiToken;
      const originalUrl = useAppStore.getState().apiUrl;
      
      useAppStore.getState().setApiToken(testToken);
      useAppStore.getState().setApiUrl(apiUrlToUse);

      // Try to call the version endpoint to validate the token
      try {
        await api.getVersion();
        // Success - token is valid
        setApiToken(testToken);
        setApiUrl(apiUrlToUse);
        setError(null);
      } catch (err) {
        // Restore original values
        useAppStore.getState().setApiToken(originalToken);
        useAppStore.getState().setApiUrl(originalUrl);
        
        if (err instanceof Error) {
          if (err.message.includes("401") || err.message.includes("403")) {
            setError("Invalid API token. Please check your credentials.");
          } else if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
            setError(`Cannot connect to API at ${apiUrlToUse}. Please check the URL.`);
          } else {
            setError(`Authentication failed: ${err.message}`);
          }
        } else {
          setError("Failed to validate token. Please check your credentials.");
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Token validation error:", err);
    } finally {
      setIsValidating(false);
    }
  };

  if (apiToken) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md shadow-lg border-border/50">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl">Welcome to Plumcot</CardTitle>
            <CardDescription className="mt-2">
              Enter your API credentials to access the agent system
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="api-url" className="text-sm font-medium">
                API URL
              </Label>
              <Input
                id="api-url"
                type="text"
                placeholder="http://localhost:8080"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-token" className="text-sm font-medium">
                API Token
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="api-token"
                  type="password"
                  placeholder="Enter your bearer token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                  className="h-10 pl-9"
                />
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full h-10 font-medium"
              disabled={isValidating}
            >
              {isValidating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                "Connect to API"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

